import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang, ui, tx } from "../i18n";
import { Icon } from "./Icon";

function MaskedLine({ text, delay, accent, lowBandwidth }) {
  if (lowBandwidth) {
    return (
      <span className="mask-line">
        <span className={accent ? "text-amber-600" : ""}>{text}</span>
      </span>
    );
  }
  return (
    <span className="mask-line">
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
        className={accent ? "text-amber-600" : ""}
      >
        {text}
      </motion.span>
    </span>
  );
}

export function Hero({ lowBandwidth, onNavigate }) {
  const { lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yIsland = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -30]);
  const lines = ui.hero.lines[lang];

  return (
    <section ref={ref} data-testid="hero" className="relative overflow-hidden bg-[#F8FAFC] border-b border-slate-300">
      <motion.svg
        style={lowBandwidth ? {} : { y: yIsland }}
        viewBox="0 0 1200 400"
        aria-hidden="true"
        className="deco pointer-events-none absolute -right-24 -bottom-16 w-[110%] sm:w-[70%] opacity-[0.5]"
      >
        <path
          d="M40 210 C 120 160, 260 140, 420 150 S 720 120, 900 150 S 1140 190, 1180 230 L 1180 400 L 40 400 Z"
          fill="none"
          stroke="#1C2B4A"
          strokeOpacity="0.18"
          strokeWidth="2"
        />
        <path
          d="M40 250 C 160 210, 320 200, 500 210 S 820 180, 1000 210 S 1160 250, 1180 270 L 1180 400 L 40 400 Z"
          fill="none"
          stroke="#D97706"
          strokeOpacity="0.25"
          strokeWidth="2"
        />
        <circle cx="980" cy="120" r="60" fill="none" stroke="#1C2B4A" strokeOpacity="0.12" strokeWidth="2" />
        <circle cx="980" cy="120" r="100" fill="none" stroke="#1C2B4A" strokeOpacity="0.08" strokeWidth="2" />
      </motion.svg>

      <motion.div style={lowBandwidth ? {} : { y: yText }} className="relative px-4 sm:px-8 lg:px-12 pt-14 sm:pt-20 pb-14 sm:pb-20 max-w-6xl">
        <p data-testid="hero-kicker" className="flex items-center gap-2.5 font-mono-pr text-xs font-bold uppercase tracking-[0.2em] text-slate-600 mb-6">
          <span className="w-2.5 h-2.5 rounded-full bg-green-700 animate-pulse-dot" />
          {tx(ui.hero.kicker, lang)}
        </p>
        <div className="flex items-center gap-6 lg:gap-10">
          <h1 className="flex-1 font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-8xl text-[#1C2B4A]">
            {lines.map((line, i) => (
              <MaskedLine key={`${lang}-${i}`} text={line} delay={0.15 + i * 0.14} accent={i === 1} lowBandwidth={lowBandwidth} />
            ))}
          </h1>
          {lowBandwidth ? (
            <img src="/logo.png" alt="preparatepr.org — Puerto Rico, Preparado Siempre" data-testid="hero-logo" className="w-24 shrink-0 sm:w-36" />
          ) : (
            <motion.img
              src="/logo.png"
              alt="preparatepr.org — Puerto Rico, Preparado Siempre"
              data-testid="hero-logo"
              initial={{ scale: 0.6, opacity: 0, rotate: -8 }}
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              transition={{ duration: 0.9, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
              className="w-36 shrink-0 drop-shadow-xl sm:w-52 lg:w-72"
            />
          )}
        </div>
        <p data-testid="hero-subtitle" className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-700">
          {tx(ui.hero.sub, lang)}
        </p>
        <div className="mt-9 flex flex-wrap gap-3">
          <button
            data-testid="hero-cta-directory"
            onClick={() => onNavigate("directorio")}
            className="group inline-flex items-center gap-2 rounded-md bg-[#1C2B4A] px-6 py-3.5 text-base font-bold text-white transition-colors duration-200 hover:bg-amber-700 active:scale-[0.98]"
          >
            <Icon name="PhoneCall" className="w-5 h-5" />
            {tx(ui.hero.cta1, lang)}
            <Icon name="ChevronRight" className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-cta-checklists"
            onClick={() => onNavigate("checklists")}
            className="inline-flex items-center gap-2 rounded-md border-2 border-[#1C2B4A] px-6 py-3.5 text-base font-bold text-[#1C2B4A] transition-colors duration-200 hover:bg-[#1C2B4A] hover:text-white active:scale-[0.98]"
          >
            <Icon name="CheckCircle2" className="w-5 h-5" />
            {tx(ui.hero.cta2, lang)}
          </button>
        </div>
      </motion.div>
    </section>
  );
}
